// Device Characterization and T1 (Readout) Simulation: the desktop's forms (raw text in every field, validated on
// the server by the desktop's own code), for the 2D tabs.
import { post } from "./api";
import type { LimitInfo } from "./transport";

export interface Choice { value: string; label: string }
export interface NamedParam { name: string; label: string }

export interface CharForm {
  tab: string;
  orientation: string;
  algorithm: string;
  values: Record<string, string>;
  floating: Record<string, boolean>;
  bounds_min: Record<string, string>;
  bounds_max: Record<string, string>;
  collective_b_min: string;
  collective_b_max: string;
  files: CharFile[];
}

export interface CharFile {
  file_id: string;
  name: string;
  branch: string;
  field_unit: string;
  energy_unit: string;
  relative: boolean;
  weight: string;
  b_min: string;
  b_max: string;
  b_sign: string;
}

export interface T1Form {
  tab: string;
  values: Record<string, string>;
  rates: Record<string, string>;
  temp_mK: string;
  protocol: string;
  n_runs: string;
  n_time_steps: string;
  read_time: string;
  read_dt: string;
  b1: string;
  b3: string;
  load_t_min: string;
  load_t_max: string;
  load_t_n: string;
  b_scan_max: string;
  b_scan_n: string;
  seed: string;
}

export interface DeviceForms {
  characterize: {
    tabs: Record<string, Omit<CharForm, "tab" | "files">>;
    params: NamedParam[];
    orientations: Choice[];
    algorithms: Choice[];
    branches: Choice[];
    b_signs: Choice[];
    field_units: string[];
    energy_units: string[];
    default_floating: Record<string, string[]>;
  };
  t1sim: {
    tabs: Record<string, Omit<T1Form, "tab">>;
    params: NamedParam[];
    rates: NamedParam[];
    protocols: Choice[];
    gamma_eff_protocols: string[];
  };
}

export interface CharDataset { index: number; name: string; label: string; b: (number | null)[]; e: (number | null)[]; included: boolean[] }

export interface CharCheck {
  ok: boolean;
  param_errors: Record<string, string>;
  file_errors: Record<string, string>;          // by the file's position in the form
  general_error: string;
  datasets: CharDataset[];
  limit: LimitInfo | null;
}

export interface BandFileInfo {
  ok: boolean;
  name: string;
  error?: string;
  n_points?: number;
  field_unit?: string;
  energy_unit?: string;
}

export interface SlopeEstimate {
  ok: boolean;
  error?: string;
  gs?: number | null;
  gv?: number | null;
  values?: Record<string, string>;
  message?: string;
}

export interface T1Check {
  ok: boolean;
  errors: Record<string, string>;
  general_error: string;
  limit: LimitInfo | null;
  msteps: number | null;
  estimate_s: number | null;
  auto: { bt1?: number | null; b_scan_max?: number; b1?: number | null; b3?: number | null; region_note?: string };
}

export const checkCharacterize = (params: CharForm) => post<CharCheck>("/transport/characterize/check", { params });
export const estimateSlopes = (params: CharForm) => post<SlopeEstimate>("/transport/characterize/estimate", { params });
export const inspectBandFile = (file_id: string) => post<BandFileInfo>("/transport/bandfile", { file_id });
export const checkT1 = (params: T1Form) => post<T1Check>("/transport/t1/check", { params });

/** The desktop's starting state of a tab's Device Characterization form (no files). */
export function freshCharForm(forms: DeviceForms, tab: string): CharForm {
  const t = forms.characterize.tabs[tab];
  return { tab, ...structuredClone(t), files: [] };
}

export function freshT1Form(forms: DeviceForms, tab: string): T1Form {
  return { tab, ...structuredClone(forms.t1sim.tabs[tab]) };
}

/** Changing the orientation also resets which parameters are floated, as on the desktop. */
export function withOrientation(form: CharForm, orientation: string, forms: DeviceForms): CharForm {
  const float = forms.characterize.default_floating[orientation] ?? [];
  return { ...form, orientation, floating: Object.fromEntries(forms.characterize.params.map((p) => [p.name, float.includes(p.name)])) };
}

/** A new file row as the desktop's "Add band file" makes it: the last level's branch (or level 0), units from its header. */
export function newFileRow(form: CharForm, file_id: string, name: string, info?: BandFileInfo): CharFile {
  const last = form.files[form.files.length - 1]?.branch;
  const branch = last && !last.includes("-") ? last : "0";
  return {
    file_id, name, branch, field_unit: info?.field_unit ?? "T", energy_unit: info?.energy_unit ?? "eV", relative: true,
    weight: "1", b_min: "0", b_max: "", b_sign: "both",
  };
}

/** The four fitted parameters as form text (for "Apply fitted values to Energy Bands / Transport"). */
export function fittedText(params: Record<string, number | null>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) if (v != null) out[k] = String(Number(v.toPrecision(10)));
  return out;
}

export function formatSeconds(s: number): string {
  if (s < 60) return `${Math.max(1, Math.round(s))} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}
